import React, { ReactNode, useState, useRef, useEffect } from 'react';

interface AccordionProps {
  title: string;
  children: ReactNode;
  isExpanded?: boolean;
  onToggle?: () => void;
  className?: string;
  titleClassName?: string;
  contentClassName?: string;
  'data-testid'?: string;
}

const Accordion: React.FC<AccordionProps> = ({
  title,
  children,
  isExpanded = false,
  onToggle,
  className = '',
  titleClassName = '',
  contentClassName = '',
  'data-testid': testId,
  ...rest
}) => {
  const [isOpen, setIsOpen] = useState(isExpanded);
  const contentRef = useRef<HTMLDivElement>(null);

  // Update local state when prop changes
  useEffect(() => {
    setIsOpen(isExpanded);
  }, [isExpanded]);

  const handleToggle = () => {
    const newState = !isOpen;
    setIsOpen(newState);
    if (onToggle) {
      onToggle();
    }
  };

  return (
    <div className={`pb-2 ${className}`} data-testid={testId} {...rest}>
      <button
        onClick={handleToggle}
        className={`w-full flex justify-between items-center py-[14.5px] px-[16px] bg-[#F5F5F5] rounded ${titleClassName}`}
        aria-expanded={isOpen}
      >
        <span className="font-[700] text-[16px] text-[#000000]">{title}</span>
        <svg
          className={`w-5 h-5 transition-transform duration-300 ease-in-out ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          data-testid="accordion-icon"
          role="img"
          aria-hidden="true"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      <div 
        ref={contentRef}
        className={isOpen ? 'block' : 'hidden'}
        data-testid="accordion-content-wrapper"
      >
        <div className={`space-y-2 mt-2 px-2.5 ${contentClassName}`} data-testid="accordion-content-container">
          {children}
        </div>
      </div>
    </div>
  );
};

export default Accordion; 